import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Droplets, Plus, RefreshCcw } from "lucide-react";
import { getHydrationToday, logHydration, resetHydrationToday } from "../lib/api";
import { toast } from "sonner";

export default function HydrationTracker() {
  const [data, setData] = useState({ glasses: 0, goal_glasses: 8 });
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try { const d = await getHydrationToday(); setData({ ...d, goal_glasses: d.goal_glasses || 8 }); } catch {}
  };
  useEffect(() => { refresh(); }, []);

  const add = async (n = 1) => {
    if (busy) return;
    setBusy(true);
    try { await logHydration(n); await refresh(); }
    catch (e) { toast.error("Couldn't log water"); }
    finally { setBusy(false); }
  };
  const reset = async () => {
    setBusy(true);
    try { await resetHydrationToday(); await refresh(); toast.success("Hydration reset for today"); }
    finally { setBusy(false); }
  };
  const glasses = Math.max(0, Math.min(20, Number(data.glasses) || 0));
  const goal = Math.max(1, Number(data.goal_glasses) || 8);
  const pct = Math.min(100, Math.round((glasses / goal) * 100));

  return (
    <div className="surface-card border border-border rounded-[24px] p-5 relative overflow-hidden" data-testid="hydration-tracker">
      <div className="absolute -top-16 -right-16 h-44 w-44 rounded-full bg-aquamarine/20 blur-3xl" />
      <div className="relative flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="h-9 w-9 rounded-xl bg-aquamarine-soft text-foreground flex items-center justify-center">
            <Droplets size={16} className="text-aquamarine" />
          </span>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-primary font-extrabold">Hydration</p>
            <p className="font-bold text-[14px]">{glasses} / {goal} glasses</p>
          </div>
        </div>
        <button onClick={reset} className="text-muted-foreground hover:text-foreground transition" aria-label="Reset today" data-testid="hydration-reset">
          <RefreshCcw size={14} />
        </button>
      </div>

      {/* Glass row visualization */}
      <div className="relative mt-4 grid grid-cols-8 gap-2" data-testid="hydration-glasses">
        {Array.from({ length: goal }).map((_, i) => {
          const filled = i < glasses;
          return (
            <motion.button
              key={i}
              type="button"
              onClick={() => add(filled ? 0 : 1)}
              whileTap={{ scale: 0.92 }}
              className={`h-14 rounded-[10px] border-2 flex items-end justify-center overflow-hidden transition-all ${filled ? "border-aquamarine bg-aquamarine/15" : "border-border bg-background"}`}
              aria-label={`Glass ${i + 1}${filled ? ' filled' : ''}`}
              data-testid={`hydration-glass-${i}`}
            >
              <motion.span
                initial={false}
                animate={{ height: filled ? "90%" : "0%" }}
                transition={{ duration: 0.45, ease: "easeOut" }}
                className="w-full bg-gradient-to-t from-aquamarine to-aquamarine-soft"
              />
            </motion.button>
          );
        })}
      </div>

      <div className="relative mt-4 flex items-center gap-2">
        <button
          onClick={() => add(1)}
          disabled={busy}
          className="flex-1 h-11 rounded-full bg-primary text-primary-foreground font-bold text-[14px] inline-flex items-center justify-center gap-1.5 disabled:opacity-60 hover:opacity-95 transition"
          data-testid="hydration-add-glass"
        >
          <Plus size={14} /> Add a glass
        </button>
        <p className="text-[11px] text-muted-foreground font-bold tracking-wider uppercase" data-testid="hydration-pct">{pct}%</p>
      </div>
    </div>
  );
}
